import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

export type ReconnectionState = {
	isReconnecting: boolean;
	messageId: string | null;
};

export type ReconnectStoreState = {
	reconnections: Record<string, ReconnectionState>;
};

const initialReconnectionState: ReconnectionState = {
	isReconnecting: false,
	messageId: null,
};

const initialState: ReconnectStoreState = {
	reconnections: {},
};

const store = createStore({
	name: 'rovoReconnect',
	initialState,
	actions: {
		startReconnection:
			(
				conversationId: string,
				messageId: string,
			): (({ getState, setState }: StoreActionApi<ReconnectStoreState>) => boolean) =>
			({ getState, setState }): boolean => {
				const state = getState();

				// Guard: already reconnecting
				if (state.reconnections[conversationId]?.isReconnecting) {
					return false;
				}

				setState({
					reconnections: {
						...state.reconnections,
						[conversationId]: {
							isReconnecting: true,
							messageId,
						},
					},
				});

				return true;
			},

		endReconnection:
			(
				conversationId: string,
			): (({ getState, setState }: StoreActionApi<ReconnectStoreState>) => void) =>
			({ getState, setState }) => {
				const state = getState();

				setState({
					reconnections: {
						...state.reconnections,
						[conversationId]: initialReconnectionState,
					},
				});
			},
		isReconnecting:
			(conversationId: string): (({ getState }: StoreActionApi<ReconnectStoreState>) => boolean) =>
			({ getState }): boolean => {
				return getState().reconnections[conversationId]?.isReconnecting ?? false;
			},
	},
});

export const useReconnectStore: HookFunction<
	ReconnectStoreState,
	BoundActions<
		ReconnectStoreState,
		{
			startReconnection: (
				conversationId: string,
				messageId: string,
			) => ({ getState, setState }: StoreActionApi<ReconnectStoreState>) => boolean;
			endReconnection: (
				conversationId: string,
			) => ({ getState, setState }: StoreActionApi<ReconnectStoreState>) => void;
			isReconnecting: (
				conversationId: string,
			) => ({ getState }: StoreActionApi<ReconnectStoreState>) => boolean;
		}
	>,
	void
> = createHook(store);
export const useReconnectStoreActions: HookActionsFunction<
	BoundActions<
		ReconnectStoreState,
		{
			startReconnection: (
				conversationId: string,
				messageId: string,
			) => ({ getState, setState }: StoreActionApi<ReconnectStoreState>) => boolean;
			endReconnection: (
				conversationId: string,
			) => ({ getState, setState }: StoreActionApi<ReconnectStoreState>) => void;
			isReconnecting: (
				conversationId: string,
			) => ({ getState }: StoreActionApi<ReconnectStoreState>) => boolean;
		}
	>
> = createActionsHook(store);

export const useReconnectionState: HookFunction<
	ReconnectionState,
	BoundActions<
		ReconnectStoreState,
		{
			startReconnection: (
				conversationId: string,
				messageId: string,
			) => ({ getState, setState }: StoreActionApi<ReconnectStoreState>) => boolean;
			endReconnection: (
				conversationId: string,
			) => ({ getState, setState }: StoreActionApi<ReconnectStoreState>) => void;
			isReconnecting: (
				conversationId: string,
			) => ({ getState }: StoreActionApi<ReconnectStoreState>) => boolean;
		}
	>,
	string
> = createHook(store, {
	selector: (state: ReconnectStoreState, conversationId: string) =>
		state.reconnections[conversationId] ?? initialReconnectionState,
});
